import { FC, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {  RefreshCw, Clock } from "lucide-react";
import { DashboardProps, Activity, KnowledgeBaseSummary } from "../types/types";
import { DashboardCard, ActivityItem, LoadingSpinner } from "../components/common";
import { dashboardDetails, knowledgeBaseSummary } from "../services/api/dashboardService";
import { formatDateTime } from '../services/utils';

interface MockData {
  metrics: {
    knowledgeBases: number;
    knowledgeArticles: number;
    syncStatus: {
      lastSync: string;
      statusType: string;
    };
  };
  recentActivity: Activity[];
}

interface kbSummaryData {
  knowledgeBaseSummary: KnowledgeBaseSummary[],
  knowledgeArticles: number
}

const Dashboard: FC<DashboardProps> = ({ setupCompleted }) => {
  // This would normally come from an API or state management system
  const initialDashboardState: MockData = {
    metrics: {
      knowledgeBases: 0,
      knowledgeArticles: 0,
      syncStatus: {
        lastSync: "",
        statusType: "",
      },
    },
    recentActivity: [],
  };
  const initialKbSummary : kbSummaryData = {
    knowledgeBaseSummary: [],
    knowledgeArticles: 0
  }

  const [dashboardInfo, setDashboardInfo] = useState<MockData>(
    initialDashboardState
  );
  const [kbSummary, setKbSummary] = useState<kbSummaryData>(initialKbSummary);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [kbSummaryLoading, setKbSummaryLoading] = useState(true);
  const fetchDashboardDetails = async () => {
    try {
      const { data: dashboard } = await dashboardDetails();
      if (dashboard?.response) {
        const dashboardMetrics = dashboard.response?.metrics;
        const dashboardRecentActivity = dashboard.response?.recentActivity;

        setDashboardInfo(prev => ({
          ...prev,
          metrics: {...dashboardMetrics},
          recentActivity: dashboardRecentActivity
        }));
        setLastUpdated(new Date());
        
      } else {
        console.log("API response", JSON.stringify(dashboard))
      }
    } catch (error) {
      console.error("Error fetching dashboard data", error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchKnowledgeBaseSummary = async () => {
    try {
      const { data: kbSummaryDetails } = await knowledgeBaseSummary();
      if (kbSummaryDetails.response) {
        const kbDetails = kbSummaryDetails.response?.knowledgeBaseSummary || [];
        const articleCount = kbSummaryDetails.response.metrics.knowledgeArticles || 0;
        setKbSummary(prev => ({
          ...prev,
          knowledgeBaseSummary: [...(kbDetails || [])],
          knowledgeArticles: articleCount
        }))
      } else {
        console.log("API response for knowledge base summary query", JSON.stringify(kbSummaryDetails))
      }
      
    } catch (error) {
      console.error("Error fetching Knowledge base summary  data", error);
    } finally {
      setKbSummaryLoading(false)
      setRefreshing(false);
    }
  }

  const handleRefresh = async () => {
    setRefreshing(true);
    fetchDashboardDetails();
    fetchKnowledgeBaseSummary();
  };

  useEffect(() => {
    fetchDashboardDetails();
    fetchKnowledgeBaseSummary();
  }, []);

  if (loading) {
    return <LoadingSpinner title="Loading Dashboard" message="Fetching your dashboard data..." />;
  }

  return (
    <div className="pt-16 ml-64">
      <div className="p-6">
        {/* Last Modified and Refresh */}
        <div className="flex justify-end items-center mb-6">
          <div className="flex items-center gap-4">
            <div className="flex items-center text-sm text-gray-500">
              <Clock size={16} className="mr-1" />
              Last updated: {lastUpdated.getFullYear()}-{String(lastUpdated.getMonth() + 1).padStart(2, '0')}-{String(lastUpdated.getDate()).padStart(2, '0')} {String(lastUpdated.getHours()).padStart(2, '0')}:{String(lastUpdated.getMinutes()).padStart(2, '0')}
            </div>
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Refresh dashboard"
            >
              <RefreshCw size={16} className={`mr-1 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Knowledge Base Connector Status */}
        <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
          <div className="flex justify-between items-center mb-2">
            <h2 className="text-lg font-medium">
              Knowledge Base Connector Status
            </h2>
            <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
              Operational
            </span>
          </div>
          <p className="text-gray-600">
            Welcome to the Knowledge Base Connector for SharePoint. This tool
            allows you to seamlessly sync your SharePoint content with Genesys
            Cloud Knowledge Base.
          </p>
        </div>

        {/* Status Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          <DashboardCard
            title="Integration Status"
            value={setupCompleted ? "Configured" : "Setup Required"}
            status={setupCompleted ? "success" : "warning"}
          />

          <DashboardCard
            title="Knowledge Bases"
            value={dashboardInfo?.metrics?.knowledgeBases?.toString() || '0'}
          />
          <DashboardCard
            title="Knowledge Articles"
            value={
              kbSummaryLoading ? 
              <LoadingSpinner title="Loading Knowledege base article count" message="Fetching your article count for KB"   size = 'small' fullScreen={false}/> :
              kbSummary?.knowledgeArticles.toString() || '0'}
          />
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-sm font-medium text-gray-500">Last Sync</h3>
            </div>
            <p 
              className="text-2xl font-semibold text-gray-800"
              title={dashboardInfo?.metrics?.syncStatus?.lastSync 
                ? new Date(dashboardInfo.metrics.syncStatus.lastSync).toLocaleString()
                : 'No sync performed yet'}
            >
              {dashboardInfo?.metrics?.syncStatus?.lastSync 
                ? formatDateTime(dashboardInfo.metrics.syncStatus.lastSync) 
                : 'Never'}
            </p>
          </div>
        </div>

        {/* Activity and Quick Actions */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Recent Activity */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-medium">Recent Activity</h2>
              <Link
                to="/logs"
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                View All
              </Link>
            </div>
            <div className="space-y-4">
              {dashboardInfo.recentActivity.length > 0 ? dashboardInfo.recentActivity.map((activity, index) => (
                <ActivityItem
                  key={`${activity?.actionId}_${index}`}
                  action={activity?.actionName}
                  time={formatDateTime(activity?.timestamp)}
                  status={activity.status}
                  description={activity?.details?.message}
                />        
              )) : 
              <p className="text-center text-gray-500 text-sm mt-6">
                No recent activity found. Please check back later.
              </p>}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-medium mb-4">Quick Actions</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Link to="/knowledge-base" className="block">
                <button
                  className="w-full flex items-center justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={!setupCompleted}
                >
                  Create Knowledge Base
                </button>
              </Link>
              <Link to="/sharepoint" className="block">
                <button
                  className="w-full flex items-center justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={!setupCompleted}
                >
                  Configure SharePoint
                </button>
              </Link>
              <button
                className="w-full flex items-center justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!setupCompleted}
                onClick={() => alert("Sync started")}
              >
                Run Sync Now
              </button>
              <Link to="/logs" className="block">
                <button className="w-full flex items-center justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                  View Reports
                </button>
              </Link>
            </div>
          </div>
        </div>

        {/* Knowledge Base Summary */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium">Knowledge Base Summary</h2>
          </div>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Name
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Source
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Articles
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Last Synced
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {kbSummary.knowledgeBaseSummary.length > 0 && kbSummary.knowledgeBaseSummary.map((kb) => (
                <tr key={kb.knowledgeBaseId}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {kb.knowledgeBaseName}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {(kb.source?.length ?? 0) > 0 ? (kb.source?.[0]) :  'No source found'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {kb.articleCount}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDateTime(kb.lastSync)}
                  </td>
                  
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                      {(kb.status?.length ?? 0) > 0 ? (kb.status?.[0]) :  ''}
                    </span>
                  </td>
                </tr>
              ))}
              {kbSummaryLoading ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-6 py-4 text-center text-sm text-gray-500"
                  >
                    <LoadingSpinner title="Loading Knowledege base summary" message="Fetching your knowledge base summary data..."   size = 'small' fullScreen={false}/>;
                  </td>
                </tr>
              ): kbSummary.knowledgeBaseSummary.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-6 py-4 text-center text-sm text-gray-500"
                  >
                    No knowledge bases found. Create your first knowledge base
                    to get started.
                  </td>
                </tr>
              )
              }
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
